<template>
  <DeleteConfirmDialog
    v-model:visible="hideOptimizationVisible"
    :title="i18n.t('hideOptimization')"
    :loading="hideOptimizationLoading"
    :delete-btn-label="i18n.t('submit')"
    @cancel="hideOptimizationVisible = false"
    @delete="hideOptimization(optimizationToHide)">
    <span>{{ i18n.t('hideOptimizationInfo') }}</span>
  </DeleteConfirmDialog>

  <OptimizationDialogHandler
    ref="dialogHandler"
    @hide="(id) => hideOptimization(id)"
    @unhide="(id) => unhideOptimization(id)" />

  <div class="flex gap-4">
    <ApplicationIcon
      v-if="application"
      :application-name="props.optimization.application_display_name"
      :application-type="props.optimization.application_type"
      :size="40"
      style="align-self: center"
      class="shrink-0 cursor-pointer"
      @click="openDialog()" />
    <div class="cursor-pointer" @click="openDialog()">
      <TextMultiline :text="props.optimization.application_display_name" />
      <TextMultiline
        v-if="props.showPersonName"
        :text="props.optimization.person_name"
        class="text-contrast-muted" />
      <TextMultiline
        :text="
          getReason(
            props.optimization.type as OptimizationType,
            optimization.data
          )
        "
        class="text-contrast-muted" />

      <TextMultiline
        v-if="props.optimization.savings != undefined"
        :text="
          i18n.t('savingsPotential', {
            count: savings > 0 ? getCorrectedPrice(savings) : 0,
          })
        "
        class="text-contrast-muted" />
    </div>

    <SmDropdown trigger="click" class="ml-auto" style="align-self: center">
      <SmDropdownItem :icon="Info" @click="openDialog()">
        {{ i18n.t('showDetails') }}
      </SmDropdownItem>
      <SmDropdownItem
        v-if="!props.optimization.hidden"
        :icon="EyeOff"
        @click="openConfirmDialog(props.optimization)">
        {{ i18n.t('views.optimizations.table.actions.hideOptimization') }}
      </SmDropdownItem>
      <SmDropdownItem
        v-else
        :icon="Eye"
        @click="unhideOptimization(props.optimization._id)">
        {{ i18n.t('unhideOptimization') }}
      </SmDropdownItem>
    </SmDropdown>
  </div>
</template>

<script setup lang="ts">
  import { OptimizationType } from '@/client'
  import {
    OptimizationStore,
    EnrichedOptimizations,
  } from '@/stores/optimization/optimization.store'
  import { getReason } from '@/utils/optimizations'
  import OptimizationDialogHandler from '@/components/optimizations/OptimizationDialogHandler.vue'
  import { getCorrectedPrice } from '@/utils/formatter/price'
  import { useI18n } from 'vue-i18n'
  import { sendToast } from '@/components/sm/SmNotification'
  import { Eye, EyeOff, Info } from 'lucide-vue-next'

  const props = defineProps({
    optimization: {
      type: Object as PropType<EnrichedOptimizations>,
      default: null,
    },

    application: {
      type: Boolean,
      default: false,
      required: false,
    },
    showPersonName: {
      type: Boolean,
      default: false,
      required: false,
    },
  })

  const i18n = useI18n()

  const savings = computed(() => {
    // multiple by 12 to get savings per year
    return props.optimization.savings * 12
  })

  // ---------------------------------------------------------------------------
  // Handle Dialogs
  // ---------------------------------------------------------------------------

  function openDialog() {
    if (props.optimization.person_id) {
      dialogHandler.value?.showDialog(
        props.optimization,
        props.optimization.person_id
      )
    }
  }

  // Optimization Handler
  const dialogHandler = ref<typeof OptimizationDialogHandler>()

  // Hide optimization
  const hideOptimizationVisible = ref<boolean>(false)
  const optimizationToHide = ref<string>('')
  const hideOptimizationLoading = ref<boolean>(false)

  function openConfirmDialog(optimization: EnrichedOptimizations) {
    hideOptimizationVisible.value = true
    optimizationToHide.value = optimization._id
    hideOptimizationLoading.value = false
  }

  async function hideOptimization(id: string) {
    hideOptimizationLoading.value = true
    await OptimizationStore.hideOptimization(id).then(() => {
      sendToast(i18n.t('hideOptimizationSuccess'), undefined, 'success')
      hideOptimizationLoading.value = false
      hideOptimizationVisible.value = false
      dialogHandler.value?.closeDialog()
    })
  }

  async function unhideOptimization(id: string) {
    await OptimizationStore.unhideOptimization(id).then(() => {
      sendToast(
        i18n.t('notifications.optimizationsView.showOptimizationSuccess.title'),
        undefined,
        'success'
      )
      dialogHandler.value?.closeDialog()
    })
  }

  // ensure reset
  watch(
    () => hideOptimizationVisible.value,
    (newVal) => {
      if (!newVal) {
        hideOptimizationLoading.value = false
        optimizationToHide.value = ''
      }
    }
  )
</script>
